/**
 * ProgressNoteReadOnlyDetail — presentational, strictly READ-ONLY view of a
 * single Progress Note. Renders every authorised structured field plus the
 * amendment history and strike status. Contains NO mutation calls and NO
 * edit / amend / strike / submit controls. Consumes an already-fetched note
 * object (from GET /notes/by-participant); no API calls of its own.
 */
import React from 'react';
import { Badge } from '@/components/ui/badge';

function fmt(dt) {
  if (!dt) return null;
  try { return new Date(dt).toLocaleString(); } catch { return String(dt); }
}

function Field({ label, value, testid, mono }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div data-testid={testid}>
      <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</h5>
      <p className={`text-sm whitespace-pre-wrap ${mono ? 'font-mono' : ''}`}>{value}</p>
    </div>
  );
}

export function ProgressNoteReadOnlyDetail({ note }) {
  if (!note) return null;
  const template = (note.template_key || note.note_type || '').replace(/_/g, ' ');
  const amendments = note.amendments || [];
  const struck = note.struck;

  return (
    <div className="space-y-4" data-testid="progress-note-readonly-detail">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {note.status && <Badge variant="outline" data-testid="note-ro-status">{note.status}</Badge>}
            {template && <Badge variant="outline" className="text-[10px]" data-testid="note-ro-template">{template}</Badge>}
            {struck && <Badge variant="destructive" className="text-[10px]" data-testid="note-ro-struck-badge">Struck</Badge>}
          </div>
          <h3 className="font-semibold" data-testid="note-ro-title">{note.title || 'Progress Note'}</h3>
        </div>
      </div>

      <div className="grid gap-3 border-t pt-3">
        <Field
          label="Note"
          value={note.note || note.content}
          testid="note-ro-body"
        />
        <Field label="Goals Worked Toward" value={note.goals_worked_toward} testid="note-ro-goals" />
        <Field label="Outcomes" value={note.outcomes} testid="note-ro-outcomes" />
      </div>

      <div className="border-t pt-3 space-y-2">
        <div data-testid="note-ro-risks">
          <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Risks Identified</h5>
          <p className="text-sm">{note.risks_identified ? 'Yes' : 'No'}</p>
        </div>
        <Field label="Risk Details" value={note.risk_details} testid="note-ro-risk-details" />
        <div data-testid="note-ro-followup">
          <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Requires Follow-up</h5>
          <p className="text-sm">{note.requires_follow_up ? 'Yes' : 'No'}</p>
        </div>
        <Field label="Follow-up Details" value={note.follow_up_details} testid="note-ro-followup-details" />
      </div>

      <div className="border-t pt-3 grid gap-3 sm:grid-cols-2" data-testid="note-ro-meta">
        <Field label="Author / Recorded by" value={note.author_name || 'N/A'} testid="note-ro-author" />
        <Field label="Created" value={fmt(note.created_at)} testid="note-ro-created" />
        {note.submitted_at && <Field label="Submitted" value={fmt(note.submitted_at)} testid="note-ro-submitted" />}
        <Field label="Last Updated" value={fmt(note.updated_at)} testid="note-ro-updated" />
        <Field label="Related Shift" value={note.shift_id} testid="note-ro-shift" mono />
      </div>

      <div className="border-t pt-3" data-testid="note-ro-amendments">
        <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Amendment history</h5>
        {amendments.length === 0 ? (
          <p className="text-xs text-muted-foreground italic" data-testid="note-ro-amendments-empty">No amendments recorded.</p>
        ) : (
          <ul className="space-y-1.5">
            {amendments.map((a, i) => (
              <li key={a.seq ?? i} className="text-sm" data-testid={`note-ro-amendment-${a.seq ?? i}`}>
                <span className="font-medium text-foreground">Amendment {a.seq ?? i + 1}</span>
                {a.actor_name ? <> by {a.actor_name}</> : null}
                {a.amended_at ? <span className="text-muted-foreground"> · {fmt(a.amended_at)}</span> : null}
                {a.reason ? <span className="text-muted-foreground"> — reason: {a.reason}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {struck && (
        <div className="border-t pt-3" data-testid="note-ro-strike-history">
          <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Strike status</h5>
          <p className="text-sm text-red-600">
            Struck{struck.actor_name ? <> by {struck.actor_name}</> : null}
            {struck.struck_at ? <> · {fmt(struck.struck_at)}</> : null}
            {struck.reason ? <> — reason: {struck.reason}</> : null}
          </p>
        </div>
      )}
    </div>
  );
}

export default ProgressNoteReadOnlyDetail;
